import useEditNotificationRecipients from "@entities/organisation/lib/hooks/useAddNotificationRecipients";
import useDeleteNotificationRecipients from "@entities/organisation/lib/hooks/useDeleteNotificationRecipients";
import useEditAdRightSettings from "@entities/organisation/lib/hooks/useEditAdRightSettings";
import useGetOrganisationBrands from "@entities/organisation/lib/hooks/useGetOrganisationBrands";
import useGetOrganisationById from "@entities/organisation/lib/hooks/useGetOrganisationById";
import { useSearchBrandUser } from "@entities/organisation/lib/hooks/useSearchBrandUser";
import { AdRightOptions } from "@shared/lib/constants";
import { ToastService } from "@shared/lib/services";
import { ACCOUNT_STATUS } from "@shared/lib/types";
import { INotificationRecipient } from "@shared/lib/types/brandOrganisation";
import { Alert, Button, Popconfirm, Tag } from "@shared/ui";
import { DisabledPopover } from "@shared/ui/DisabledPopover";
import { Flex, Form, InputNumber, Radio, Select, Spin } from "antd";
import { Bell, CircleAlert, Globe, Lock, Save, Trash2 } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useDebounce } from "use-debounce";
import styles from "./styles.module.scss";

export const AdRightsTab = ({
  brandOrganisationId,
}: {
  brandOrganisationId: string;
}) => {
  const { organisation: organisationData, isLoading: isOrganisationFetching } =
    useGetOrganisationById(brandOrganisationId || "");
  const { organisationBrands, isOrganisationBrandsFetching } =
    useGetOrganisationBrands(brandOrganisationId);
  const {
    editAdRightSettings,
    isEditAdRightSettingsLoading,
    isEditAdRightSettingsSuccess,
    editAdRightSettingsError,
  } = useEditAdRightSettings();
  const {
    editNotificationRecipients,
    isEditNotificationRecipientsLoading,
    isEditNotificationRecipientsSuccess,
    editNotificationRecipientsError,
  } = useEditNotificationRecipients();

  const {
    deleteNotificationRecipients,
    isDeleteNotificationRecipientsLoading,
    isDeleteNotificationRecipientsSuccess,
    deleteNotificationRecipientsError,
  } = useDeleteNotificationRecipients();
  const [form] = Form.useForm();
  const adRightsSettings = Form.useWatch("adRightsSettings", form);
  const customOffsetInDays = Form.useWatch("customOffsetInDays", form);
  const [deleteNotificationRecipientsId, setDeleteNotificationRecipientsId] =
    React.useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery] = useDebounce(searchQuery, 500);
  const { employeesData, isFetchingEmployees } = useSearchBrandUser({
    brandOrganisationId: brandOrganisationId || "",
    pageNo: 1,
    limit: 100,
    searchQuery: debouncedSearchQuery,
    accountStatuses: [ACCOUNT_STATUS.ACTIVE],
  });

  const existingRecipientUserIds = useMemo(() => {
    const list =
      organisationData?.adRightsSettings?.alertNotificationReciepents ?? [];
    return new Set(list.map((r) => r.user._id));
  }, [organisationData?.adRightsSettings?.alertNotificationReciepents]);

  const employeeSelectOptions = useMemo(() => {
    const employees = employeesData?.data?.data ?? [];
    return employees
      .filter((e) => !existingRecipientUserIds.has(e._id))
      .map((e) => {
        const name = [e.firstName, e.lastName].filter(Boolean).join(" ").trim();
        const label = e.emailId ? `${name || "—"} (${e.emailId})` : name || "—";
        return { value: e._id, label };
      });
  }, [employeesData?.data?.data, existingRecipientUserIds]);

  useEffect(() => {
    form.setFieldsValue({
      adRightsSettings: organisationData?.adRightsSettings?.settings,
      ...organisationData?.adRightsSettings,
    });
  }, [organisationData]);
  useEffect(() => {
    if (isEditAdRightSettingsSuccess) {
      ToastService.success("Ad rights settings updated successfully");
    }
    if (editAdRightSettingsError) {
      ToastService.error(editAdRightSettingsError?.message);
    }
  }, [isEditAdRightSettingsSuccess, editAdRightSettingsError]);

  useEffect(() => {
    if (isEditNotificationRecipientsSuccess) {
      ToastService.success("Notification recipients updated successfully");
      form.setFieldsValue({
        notificationReciepents: {
          userId: undefined,
          brandIds: "",
        },
      });
    }
    if (editNotificationRecipientsError) {
      ToastService.error(editNotificationRecipientsError?.message);
    }
  }, [isEditNotificationRecipientsSuccess, editNotificationRecipientsError]);

  useEffect(() => {
    if (isDeleteNotificationRecipientsSuccess) {
      ToastService.success("Notification recipients deleted successfully");
    }
    if (deleteNotificationRecipientsError) {
      ToastService.error(deleteNotificationRecipientsError?.message);
    }
  }, [
    isDeleteNotificationRecipientsSuccess,
    deleteNotificationRecipientsError,
  ]);

  const submitAdRights = () => {
    editAdRightSettings({
      brandOrganisationId: brandOrganisationId || "",
      adRightsSettings: adRightsSettings,
      customOffsetInDays: parseInt(customOffsetInDays) || 0,
    });
  };

  const submitNotificationRecipients = async () => {
    try {
      await form.validateFields([
        ["notificationReciepents", "userId"],
        ["notificationReciepents", "brandIds"],
      ]);
    } catch {
      return;
    }
    const notificationReciepents =
      form.getFieldValue("notificationReciepents") || {};
    editNotificationRecipients({
      brandOrganisationId: brandOrganisationId || "",
      notificationReciepents: {
        userId: notificationReciepents.userId,
        isAllBrands: notificationReciepents.brandIds === "All Brands",
        brandIds: [notificationReciepents.brandIds].filter(
          (id) => id !== "All Brands"
        ),
      },
    });
  };

  const onRemoveRecipient = (recipientId: string) => {
    deleteNotificationRecipients({
      brandOrganisationId: brandOrganisationId || "",
      notificationReciepentsId: recipientId,
    });
  };

  if (isOrganisationFetching) {
    return (
      <Flex justify="center" style={{ width: "100%" }}>
        <Spin />
      </Flex>
    );
  }

  const isFormDisabled =
    adRightsSettings === AdRightOptions["Custom-Offset"] &&
    (customOffsetInDays <= 0 || customOffsetInDays > 30);

  return (
    <Form
      form={form}
      initialValues={{
        adRightsSettings: organisationData?.adRightsSettings?.settings,
        ...organisationData?.adRightsSettings,
      }}
    >
      <div className={styles.container}>
        {/* Duration Settings */}
        <section className={styles.section}>
          <div className={styles.header}>
            <div>
              <h3 className={styles.title}>
                Ad Rights Duration{" "}
                <Lock size={14} className={styles.lockIcon} />
              </h3>
              <p className={styles.subtitle}>
                Configure when the usage rights period officially begins for
                your campaigns.
              </p>
            </div>
            {/* <Tag style={{ height: "fit-content", fontSize: 11 }}>
              Contact Account Manager to change
            </Tag> */}
          </div>
          <div className={styles.content}>
            <Form.Item name="adRightsSettings">
              <Radio.Group className={styles.radioGroup}>
                <Radio value={AdRightOptions.Automatic}>
                  <span className={styles.radioLabel}>
                    Automatic (Recommended)
                  </span>
                  <span className={styles.radioDescription}>
                    Start date defaults to the later of:{" "}
                    <strong>Go Live Date</strong> or{" "}
                    <strong>Purchase Date</strong>.
                  </span>
                </Radio>
                <Radio value={AdRightOptions["Brand-Decision"]}>
                  <span className={styles.radioLabel}>Brand Decides</span>
                  <span className={styles.radioDescription}>
                    You manually set the start date for each asset.
                  </span>
                </Radio>
                <Radio value={AdRightOptions["Custom-Offset"]}>
                  <span className={styles.radioLabel}>Custom Offset</span>
                  <span className={styles.radioDescription}>
                    Start date is automatically set to X days after Purchase/Go
                    Live.
                  </span>
                  {adRightsSettings === AdRightOptions["Custom-Offset"] && (
                    <div className={styles.customOffset}>
                      <span>Start</span>
                      <Form.Item
                        name="customOffsetInDays"
                        rules={[
                          {
                            required: true,
                            message: "Please enter a custom offset in days",
                          },
                          {
                            validator: (rule, value, callback) => {
                              if (value <= 0) {
                                return Promise.reject(
                                  "Please enter a valid number"
                                );
                              }
                              if (value > 30) {
                                return Promise.reject(
                                  "Please enter a number less than 30"
                                );
                              }

                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <InputNumber
                          type="number"
                          className={styles.customDaysInput}
                        />
                      </Form.Item>
                      <span>days after.</span>
                    </div>
                  )}
                </Radio>
              </Radio.Group>
            </Form.Item>
          </div>
          <div className={styles.footer}>
            <DisabledPopover isDisabled={false}>
              <Button
                type="primary"
                disabled={isFormDisabled}
                icon={<Save size={16} />}
                onClick={() => submitAdRights()}
                loading={isEditAdRightSettingsLoading}
              >
                Save Changes
              </Button>
            </DisabledPopover>
          </div>
        </section>

        {/* Notification Management */}
        <section className={styles.section}>
          <div className={styles.header}>
            <div>
              <h3 className={styles.title}>
                <Bell size={16} className={styles.bellIcon} /> Notification
                Settings
              </h3>
              <p className={styles.subtitle}>
                Manage who receives alerts regarding ad rights expiration and
                renewals.
              </p>
            </div>
          </div>
          <div className={styles.content}>
            <Alert
              message={
                <span style={{ color: "#9a3412", fontSize: 13 }}>
                  <b>Policy Requirement:</b> At least 1 recipient is required to
                  ensure critical rights expiration alerts are not missed.
                </span>
              }
              type="warning"
              showIcon
              icon={<CircleAlert size={16} />}
            />
            <div className={styles.recipientList}>
              <Form.Item name="alertNotificationReciepents" noStyle />
              <Form.Item
                shouldUpdate={(prevValues: any, curValues: any) =>
                  prevValues.alertNotificationReciepents !==
                  curValues.alertNotificationReciepents
                }
                noStyle
              >
                {({ getFieldValue }) => {
                  const recipients: INotificationRecipient[] =
                    getFieldValue("alertNotificationReciepents") || [];
                  return (
                    <>
                      {recipients.map((recipient) => (
                        <div
                          key={recipient._id}
                          className={styles.recipientItem}
                        >
                          <div className={styles.recipientInfo}>
                            <div className={styles.recipientAvatar}>
                              {recipient.user.firstName.charAt(0)}
                            </div>
                            <div>
                              <div className={styles.recipientName}>
                                {recipient.user.firstName}{" "}
                                {recipient.user.lastName}
                              </div>
                              <div className={styles.recipientEmail}>
                                {recipient.user.emailId}
                              </div>
                            </div>
                          </div>
                          <div className={styles.recipientActions}>
                            <Tag>
                              {recipient?.isAllBrands
                                ? "All Brands"
                                : organisationBrands?.data?.data?.find(
                                    (brand) =>
                                      brand._id === recipient.brandIds[0]
                                  )?.name}
                            </Tag>
                            <Popconfirm
                              title="Delete the recipient"
                              description="Are you sure to delete this recipient?"
                              onConfirm={() => {
                                onRemoveRecipient(recipient._id || "");
                                setDeleteNotificationRecipientsId(
                                  recipient._id || ""
                                );
                              }}
                              okText="Yes"
                              cancelText="No"
                              disabled={recipients?.length <= 1}
                            >
                              <Button
                                danger
                                type="text"
                                size="small"
                                icon={<Trash2 size={16} />}
                                disabled={recipients?.length <= 1}
                                loading={
                                  isDeleteNotificationRecipientsLoading &&
                                  recipient._id ===
                                    deleteNotificationRecipientsId
                                }
                              />
                            </Popconfirm>
                          </div>
                        </div>
                      ))}
                    </>
                  );
                }}
              </Form.Item>
            </div>
            <div className={styles.addRecipient}>
              <h4 className={styles.addRecipientTitle}>Add Recipient</h4>
              <div>
                <Form.Item>
                  <Flex gap={10}>
                    <Form.Item
                      name={["notificationReciepents", "userId"]}
                      style={{ flex: 1 }}
                      rules={[
                        {
                          required: true,
                          message: "Please select an employee",
                        },
                      ]}
                    >
                      <Select
                        showSearch
                        allowClear
                        placeholder="Select employee"
                        options={employeeSelectOptions}
                        loading={isFetchingEmployees}
                        filterOption={false}
                        searchValue={searchQuery}
                        onSearch={setSearchQuery}
                        // onChange={(value) => {
                        //   setSearchQuery("");
                        //   form.setFieldsValue({
                        //     notificationReciepents: {
                        //       userId: value,
                        //       brandIds: "",
                        //     },
                        //   });
                        // }}
                        notFoundContent={
                          isFetchingEmployees
                            ? "Loading…"
                            : "No employees found"
                        }
                      />
                    </Form.Item>
                    <Form.Item
                      name={["notificationReciepents", "brandIds"]}
                      style={{ flex: 1 }}
                      rules={[
                        {
                          required: true,
                          message: "Please select a brand scope",
                        },
                      ]}
                    >
                      <Select
                        prefix={<Globe size={14} style={{ marginTop: 6 }} />}
                        placeholder="Brand"
                        loading={isOrganisationBrandsFetching}
                      >
                        <Select.Option value="All Brands">
                          All Brands
                        </Select.Option>
                        {organisationBrands?.data?.data?.map((brand) => (
                          <Select.Option key={brand._id} value={brand._id}>
                            {brand.name}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Flex>
                </Form.Item>
              </div>
              <Button
                type="primary"
                block
                style={{ height: 31.5 }}
                onClick={() => submitNotificationRecipients()}
                loading={isEditNotificationRecipientsLoading}
              >
                Add Recipient
              </Button>
            </div>
          </div>
        </section>
      </div>
    </Form>
  );
};
